import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, Download, CheckCircle, Lock, Activity, Building2, ChevronRight, FileText, Zap, Timer, Loader2 } from 'lucide-react';
export function SharedReportViewer() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'verify' | 'valid' | 'expired'>('loading');
  const [email, setEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const reportData = {
    type: 'personal' as const,
    score: 62,
    date: 'October 12, 2025',
    scope: 'summary_actions',
    allowDownload: true,
    sections: [{
      title: 'Family & Relationship Changes',
      score: 80
    }, {
      title: 'Wills, POA & Estate Planning',
      score: 40
    }, {
      title: 'Property & Housing',
      score: 100
    }, {
      title: 'Work, Money & Disputes',
      score: 60
    }],
    topIssues: [{
      category: 'Estate Planning',
      text: 'You do not have a Will yet — this is your most critical gap.'
    }, {
      category: 'Estate Planning',
      text: 'Missing Power of Attorney documents for incapacity planning.'
    }],
    actions: [{
      category: 'Estate Planning',
      text: 'Draft your Last Will and Testament',
      priority: 'high',
      effort: 'Medium',
      time: '1-2 hours'
    }, {
      category: 'Estate Planning',
      text: 'Create a Power of Attorney for Property',
      priority: 'high',
      effort: 'Low',
      time: '30 mins'
    }]
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setStatus('verify');
    }, 800);
    return () => clearTimeout(timer);
  }, [id]);
  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      setStatus('valid');
    }, 1000);
  };
  const handleDownload = () => {
    setIsDownloading(true);
    setTimeout(() => {
      setIsDownloading(false);
      alert('PDF Download started');
    }, 1500);
  };
  const themeColor = reportData.type === 'personal' ? 'text-violetBlue' : 'text-turquoise';
  const themeBg = reportData.type === 'personal' ? 'bg-violetBlue' : 'bg-turquoise';
  const themeLightBg = reportData.type === 'personal' ? 'bg-aliceBlue' : 'bg-turquoise/10';
  if (status === 'loading') {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center" data-id="element-4042">
        <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo" className="h-8 mb-6 animate-pulse" data-id="element-4043" />
        <div className="w-8 h-8 border-4 border-borderColor border-t-violetBlue rounded-full animate-spin" data-id="element-4044" />
      </div>;
  }
  if (status === 'expired') {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4" data-id="element-4045">
        <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo" className="h-8 mb-8" data-id="element-4046" />
        <div className="bg-white border border-borderColor rounded-2xl p-8 max-w-md w-full text-center shadow-sm" data-id="element-4047">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6" data-id="element-4048">
            <AlertCircle className="w-8 h-8 text-destructive" data-id="element-4049" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-3" data-id="element-4050">Link Expired</h1>
          <p className="text-secondary mb-6" data-id="element-4051">
            This shared report link has expired. Please ask the report owner to
            generate a new share link.
          </p>
          <a href="https://lawvo.com" className="inline-flex items-center gap-2 text-sm font-medium text-violetBlue hover:underline" data-id="element-4052">
            Visit LawVo <ChevronRight className="w-4 h-4" data-id="element-4053" />
          </a>
        </div>
        <p className="text-xs text-secondary mt-8" data-id="element-4054">
          © {new Date().getFullYear()} LawVo Inc. All rights reserved.
        </p>
      </div>;
  }
  if (status === 'verify') {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4" data-id="element-4055">
        <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo" className="h-8 mb-8" data-id="element-4056" />
        <div className="bg-white border border-borderColor rounded-2xl p-8 max-w-md w-full shadow-sm" data-id="element-4057">
          <div className="w-16 h-16 rounded-full bg-aliceBlue flex items-center justify-center mx-auto mb-6" data-id="element-4058">
            <Lock className="w-8 h-8 text-violetBlue" data-id="element-4059" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2 text-center" data-id="element-4060">
            Verify Your Identity
          </h1>
          <p className="text-sm text-secondary text-center mb-6" data-id="element-4061">
            Enter your email address to access this shared Legal Health Check
            report.
          </p>
          <form onSubmit={handleVerify} className="space-y-4" data-id="element-4062">
            <div data-id="element-4063">
              <label className="block text-sm font-medium text-primary mb-2" data-id="element-4064">
                Email Address
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" required className="w-full border border-borderColor rounded-lg px-4 py-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violetBlue/20 focus:border-violetBlue" data-id="element-4065" />
            </div>
            <button type="submit" disabled={isVerifying || !email} className="w-full bg-violetBlue hover:bg-[#6844E8] text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-4066">
              {isVerifying ? <>
                  <Loader2 className="w-4 h-4 animate-spin" data-id="element-4067" />
                  Verifying...
                </> : 'Verify & View Report'}
            </button>
          </form>
          <div className="flex items-center gap-2 mt-4 justify-center" data-id="element-4068">
            <Shield className="w-3.5 h-3.5 text-secondary" data-id="element-4069" />
            <p className="text-xs text-secondary" data-id="element-4070">Shared securely via LawVo</p>
          </div>
        </div>
        <p className="text-xs text-secondary mt-8" data-id="element-4071">
          © {new Date().getFullYear()} LawVo Inc. All rights reserved.
        </p>
      </div>;
  }
  // Valid report view
  const scoreColor = reportData.score >= 70 ? 'text-green-500' : reportData.score >= 40 ? 'text-amber-500' : 'text-destructive';
  const scoreLabel = reportData.score >= 70 ? 'Strong Foundation' : reportData.score >= 40 ? 'Needs Review' : 'Action Recommended';
  const scoreBgColor = reportData.score >= 70 ? 'bg-green-500' : reportData.score >= 40 ? 'bg-amber-500' : 'bg-destructive';
  return <div className="min-h-screen bg-gray-50" data-id="element-4072">
      {/* Header */}
      <header className="bg-white border-b border-borderColor" data-id="element-4073">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between" data-id="element-4074">
          <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo" className="h-8" data-id="element-4075" />
          {reportData.allowDownload && <button onClick={handleDownload} disabled={isDownloading} className={`flex items-center gap-2 px-4 py-2 ${themeBg} text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium disabled:opacity-50`} data-id="element-4076">
              {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-4077" /> : <Download className="w-4 h-4" data-id="element-4078" />}
              {isDownloading ? 'Preparing...' : 'Download PDF'}
            </button>}
        </div>
      </header>

      {/* Shared Banner */}
      <div className="bg-aliceBlue border-b border-borderColor" data-id="element-4079">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between" data-id="element-4080">
          <div className="flex items-center gap-2" data-id="element-4081">
            <Shield className="w-4 h-4 text-violetBlue" data-id="element-4082" />
            <span className="text-xs font-medium text-primary" data-id="element-4083">
              Shared securely via LawVo
            </span>
          </div>
          <span className="text-xs text-secondary" data-id="element-4084">
            {reportData.allowDownload ? 'View & Download' : 'View only'}
          </span>
        </div>
      </div>

      {/* Report Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-4085">
        {/* Title */}
        <div className="text-center mb-10" data-id="element-4086">
          <div className={`inline-flex items-center justify-center w-14 h-14 ${themeLightBg} rounded-full mb-4`} data-id="element-4087">
            {reportData.type === 'personal' ? <Activity className="w-7 h-7 text-violetBlue" data-id="element-4088" /> : <Building2 className="w-7 h-7 text-turquoise" data-id="element-4089" />}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-2" data-id="element-4090">
            {reportData.type === 'personal' ? 'Personal' : 'Business'} Legal
            Health Check Report
          </h1>
          <p className="text-sm text-secondary" data-id="element-4091">
            Completed on {reportData.date}
          </p>
        </div>

        {/* Score */}
        <div className="bg-white border border-borderColor rounded-2xl p-8 mb-8 shadow-sm text-center" data-id="element-4092">
          <div className="relative inline-block mb-4" data-id="element-4093">
            <svg className="w-32 h-32 transform -rotate-90" data-id="element-4094">
              <circle cx="64" cy="64" r="56" stroke="#E8EEF4" strokeWidth="10" fill="transparent" data-id="element-4095" />
              <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="10" fill="transparent" strokeDasharray={352} strokeDashoffset={352 - 352 * reportData.score / 100} className={scoreColor} data-id="element-4096" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-4097">
              <span className="text-3xl font-bold text-primary" data-id="element-4098">
                {reportData.score}
              </span>
              <span className="text-xs text-secondary" data-id="element-4099">/ 100</span>
            </div>
          </div>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide ${reportData.score >= 70 ? 'bg-green-100 text-green-800' : reportData.score >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-4100">
            <AlertCircle className="w-3.5 h-3.5" data-id="element-4101" />
            {scoreLabel}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white border border-borderColor rounded-2xl p-6 mb-8 shadow-sm" data-id="element-4102">
          <h2 className="text-lg font-bold text-primary mb-4" data-id="element-4103">
            Category Breakdown
          </h2>
          <div className="space-y-4" data-id="element-4104">
            {reportData.sections.map((sec, idx) => <div key={idx} data-id="element-4105">
                <div className="flex items-center justify-between mb-1" data-id="element-4106">
                  <span className="text-sm font-medium text-primary" data-id="element-4107">
                    {sec.title}
                  </span>
                  <span className="text-sm font-bold text-primary" data-id="element-4108">
                    {sec.score}%
                  </span>
                </div>
                <div className="w-full bg-borderColor rounded-full h-2" data-id="element-4109">
                  <div className={`h-2 rounded-full ${sec.score >= 70 ? 'bg-green-500' : sec.score >= 40 ? 'bg-amber-500' : 'bg-destructive'}`} style={{
                width: `${sec.score}%`
              }} data-id="element-4110" />
                </div>
              </div>)}
          </div>
        </div>

        {/* Top Issues */}
        <div className="bg-white border border-borderColor rounded-2xl p-6 mb-8 shadow-sm" data-id="element-4111">
          <h2 className="text-lg font-bold text-primary mb-4" data-id="element-4112">
            Top Priority Issues
          </h2>
          <div className="space-y-3" data-id="element-4113">
            {reportData.topIssues.map((issue, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-4114">
                <div className="w-2 h-2 rounded-full bg-destructive mt-2 flex-shrink-0" data-id="element-4115" />
                <div data-id="element-4116">
                  <p className="text-sm font-medium text-primary" data-id="element-4117">
                    {issue.text}
                  </p>
                  <p className="text-xs text-secondary" data-id="element-4118">{issue.category}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Action Plan */}
        {reportData.scope !== 'summary' && <div className="bg-white border border-borderColor rounded-2xl p-6 mb-8 shadow-sm" data-id="element-4119">
            <h2 className="text-lg font-bold text-primary mb-4" data-id="element-4120">
              Recommended Actions
            </h2>
            <div className="space-y-4" data-id="element-4121">
              {reportData.actions.map((action, idx) => <div key={idx} className="p-4 bg-aliceBlue rounded-xl border border-borderColor" data-id="element-4122">
                  <p className="text-sm font-semibold text-primary mb-2" data-id="element-4123">
                    {action.text}
                  </p>
                  <div className="flex items-center gap-3" data-id="element-4124">
                    <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-white px-2 py-1 rounded-md" data-id="element-4125">
                      <Zap className="w-3 h-3" data-id="element-4126" /> Effort: {action.effort}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-secondary font-medium bg-white px-2 py-1 rounded-md" data-id="element-4127">
                      <Timer className="w-3 h-3" data-id="element-4128" /> Time: {action.time}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>}

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8" data-id="element-4129">
          <p className="text-xs text-amber-800 leading-relaxed" data-id="element-4130">
            <strong data-id="element-4131">Disclaimer:</strong> This report is generated by LawVo for
            informational purposes only. It does not constitute legal advice and
            should not be relied upon as a substitute for consultation with a
            qualified lawyer licensed in your province.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center py-6 border-t border-borderColor" data-id="element-4132">
          <img src="https://cdn.magicpatterns.com/uploads/u4AKTM42BTBGHrLv1PiYxT/Logo_3.png" alt="LawVo" className="h-6 mx-auto mb-3" data-id="element-4133" />
          <p className="text-xs text-secondary" data-id="element-4134">
            © {new Date().getFullYear()} LawVo Inc. All rights reserved.
          </p>
          <p className="text-xs text-secondary mt-1" data-id="element-4135">
            This report is confidential and intended for the recipient only.
          </p>
        </div>
      </main>
    </div>;
}